import { PublicClientApplication, InteractionRequiredAuthError, AccountInfo } from '@azure/msal-browser';
import { msalConfig, getApiScopes, isAuthEnabled } from './authConfig';

/**
 * MSAL instance for authentication
 */
let msalInstance: PublicClientApplication | null = null;

/**
 * Set the MSAL instance (called from main.tsx)
 */
export const setMsalInstance = (instance: PublicClientApplication): void => {
  msalInstance = instance;
};

/**
 * Initialize MSAL instance
 */
export const initializeMsal = async (): Promise<PublicClientApplication | null> => {
  if (!isAuthEnabled()) {
    console.info('Azure AD authentication is not configured');
    return null;
  }

  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
  }
  return msalInstance;
};

/**
 * Get the current MSAL instance
 */
export const getMsalInstance = (): PublicClientApplication | null => {
  return msalInstance;
};

/**
 * Get the active account
 */
export const getActiveAccount = (): AccountInfo | null => {
  if (!msalInstance) return null;
  
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    return accounts[0];
  }
  return null;
};

/**
 * Acquire token silently for API calls.
 * Falls back to redirect (not popup) when interaction is required,
 * consistent with the app's redirect-based login flow.
 */
export const acquireTokenSilent = async (): Promise<string | null> => {
  if (!msalInstance || !isAuthEnabled()) {
    return null;
  }

  const account = getActiveAccount();
  if (!account) {
    return null;
  }

  const scopes = getApiScopes();
  if (scopes.length === 0) {
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes,
      account,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Token expired or requires interaction - use redirect (popups may be blocked)
      try {
        await msalInstance.acquireTokenRedirect({
          scopes,
          account,
        });
        // Page navigates away to Azure AD - this line is not reached
        return null;
      } catch (redirectError) {
        console.error('Failed to acquire token via redirect:', redirectError);
        return null;
      }
    }
    console.error('Failed to acquire token silently:', error);
    return null;
  }
};

/**
 * Login with redirect
 */
export const loginWithRedirect = async (): Promise<void> => {
  if (!msalInstance) {
    console.error('MSAL not initialized');
    return;
  }

  await msalInstance.loginRedirect({
    scopes: ['User.Read', ...getApiScopes()],
  });
};

/**
 * Logout
 */
export const logout = async (): Promise<void> => {
  if (!msalInstance) {
    console.error('MSAL not initialized');
    return;
  }

  await msalInstance.logoutRedirect();
};

/**
 * Clear MSAL accounts locally without triggering Azure AD logout redirect.
 * Used for cross-tab logout detection where the logout already happened in another tab.
 */
export const clearMsalAccounts = (): void => {
  if (!msalInstance) {
    return;
  }

  // Clear active account
  msalInstance.setActiveAccount(null);

  // Clear MSAL cache from localStorage (matching cacheLocation: 'localStorage' in msalConfig)
  // MSAL stores its cache with keys that start with 'msal.'
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('msal.')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};
